import { AddressElement, CardNumberElement, Elements } from "@stripe/react-stripe-js";
import type { SetupIntentResult, Stripe, StripeElements } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useStyletron } from "baseui";
import React from "react";

const stripePublishableKey =
  process.env.NODE_ENV === "production"
    ? "pk_live_51Lk6fkAFhowPbjA5vhgsxf2CetoqLuXb6M6pfPhZcQfZ7JJA9mJQku7XgKx1EdPsKSrYq8X5KyIZgw4Z4ap43Hdd00VMaa0Lhw"
    : "pk_test_51Lk6fkAFhowPbjA5Nl2Sffig0NzK3iX1BKuOmjIPMxttENb4fDRqv7VwHpscmFhmoqAt8dgoMI99T28TyC9CYV4e00RdhJp9oQ";

export const stripe = loadStripe(stripePublishableKey);

export const StripeContext = React.createContext<Promise<Stripe | null>>(Promise.resolve(null));

export const StripeProvider = (props: { children: React.ReactNode }) => {
  return <StripeContext.Provider value={stripe}>{props.children}</StripeContext.Provider>;
};

export const StripeElementsProvider = (props: { stripe: Promise<Stripe | null>; children: React.ReactNode }) => {
  const [_, $theme] = useStyletron();

  return (
    <Elements
      stripe={stripe}
      options={{
        loader: "never",
        fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inconsolata:wght@400;500;600;700&display=swap" }],
        appearance: {
          theme: "none",
          variables: {
            fontFamily: $theme.typography.body.fontFamily,
            fontSizeBase: $theme.typography.body.fontSize as string,
            fontWeightNormal: "500",
            borderRadius: $theme.borders.radius200,
            colorPrimaryText: $theme.colors.primary,
            colorBackground: $theme.colors.primary50,
            colorDanger: $theme.colors.negative400,
          },
          rules: {
            ".Input": {
              fontWeight: "500",
            },
            ".Input:focus": {
              outline: `${$theme.sizing.scale0} solid ${$theme.colors.accent}`,
            },
          },
        },
      }}
    >
      {props.children}
    </Elements>
  );
};

export const useConfirmSetupIntent = <T,>(
  onConfirm: (paymentMethodId: string) => Promise<{ success: false; message: string } | { success: true; result: T }>
) => {
  return async (
    stripe: Stripe,
    elements: StripeElements,
    setupIntentSecret: string,
    onSubmit?: () => void
  ): Promise<{ success: false; message?: string } | ({ success: true; message?: undefined } & T)> => {
    const card = elements?.getElement(CardNumberElement);
    const address = elements?.getElement(AddressElement);

    if (!card || !address) {
      throw new Error("Cannot find Stripe address or card elements");
    }

    const { value: addressElementValue } = await (address as any).getValue();

    if (onSubmit) onSubmit();

    let confirmResult: SetupIntentResult;

    if (typeof window !== "undefined" && (window as any).__mockSetupIntentResult) {
      console.debug("[Stripe] Mocking setup intent result", (window as any).__mockSetupIntentResult);
      // for testing purposes, we mock the confirm result
      confirmResult = (window as any).__mockSetupIntentResult;
    } else {
      confirmResult = await stripe.confirmCardSetup(setupIntentSecret, {
        payment_method: { card, billing_details: { ...addressElementValue } },
      });
    }

    if (
      !confirmResult ||
      confirmResult.error ||
      !confirmResult.setupIntent ||
      !(confirmResult.setupIntent.status === "succeeded") ||
      !confirmResult.setupIntent.payment_method
    ) {
      return { success: false, message: confirmResult?.error?.message };
    }

    const paymentMethod = confirmResult.setupIntent.payment_method;
    const paymentMethodId = typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id;

    const onConfirmResult = await onConfirm(paymentMethodId);

    if (onConfirmResult.success) {
      return {
        success: true,
        ...onConfirmResult.result,
      };
    } else {
      return {
        success: false,
        message: onConfirmResult.message,
      };
    }
  };
};
